import { AppButton } from "../../../components/button/AppButton";
import { Skeleton, Progress } from "antd";
import { useGetUserInfo } from "../../../hooks/useGetUserInfo";
import React, { useState } from "react";
import { BillingHistoryPage } from "./BillingHistoryPage";
import { ChoosePlanPage } from "./ChoosePlanPage";
import { PlanChangePage } from "./PlanChangePage";
import { useGetCurrentSubQuery } from "../hooks/useGetRecruiterPackage";
import {
  WarningOutlined,
  FileSearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

function formatRuleLabel(label: string): string {
  return label
    .split("_")
    .map(
      (word) =>
        word.length === 0
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
}

export const PricingComponent = () => {
  const { userInfo } = useGetUserInfo();
  const {
    data: currentSubData,
    isLoading: loadingCurrentSub,
    error: subError,
  } = useGetCurrentSubQuery();

  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [showChoosePlan, setShowChoosePlan] = useState(false);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<
    "monthly" | "annual"
  >("monthly");
  const packageName = currentSubData?.package_name || "Free";
  const packageDescription = currentSubData?.package_description || "";
  const monthlyPrice = Number(currentSubData?.package_monthly_price || 0);
  const annuallyPrice = Number(currentSubData?.package_annually_price || 0);
  const rules = currentSubData?.rules || [];

  const displayRules = (rules as Array<{
    label: string;
    packageCount: number | null;
    packageBalance: number | null;
  }>).filter((r) => r?.label && r.label !== "default");

  const usageCards = displayRules.map((rule) => {
    const total = rule.packageCount ?? 0;
    const remaining = rule.packageBalance ?? 0;
    const used = total - remaining;
    const percentage = total > 0 ? Math.round((used / total) * 100) : 0;
    const atLimit =
      rule.packageCount != null && rule.packageBalance != null
        ? rule.packageBalance === 0
        : false;
    return {
      key: rule.label,
      label: rule.label,
      icon: <FileSearchOutlined className="text-purple-500 text-lg" />,
      used,
      total,
      remaining,
      percentage,
      statusText: atLimit ? "Limit Reached" : "Within Limit",
      statusBg: atLimit ? "bg-red-100" : "bg-green-100",
      statusTextColor: atLimit ? "text-red-700" : "text-green-700",
    };
  });

  // Check if any rule is approaching limit
  const isApproachingLimit = displayRules.some((rule) => {
    const t = rule.packageCount;
    if (t == null || t <= 0) return false;
    const used = t - (rule.packageBalance ?? 0);
    return used >= t * 0.8;
  });

  const hasReachedLimit = displayRules.some(
    (r) => r.packageCount != null && r.packageBalance === 0,
  );

  // Helper functions for billing calculations
  const calculateCredit = () => {
    const remainingDays = currentSubData?.subscription?.remaining_days || 0;
    const totalDays = currentSubData?.subscription?.total_days || 31;
    const currentPrice =
      billingCycle === "monthly" ? monthlyPrice : annuallyPrice;
    return Number(((currentPrice / totalDays) * remainingDays).toFixed(2));
  };

  const calculateChargeToday = (selectedPlanPrice: number) => {
    const credit = calculateCredit();
    return Math.max(0, Number((selectedPlanPrice - credit).toFixed(2)));
  };

  const handleUpgradeClick = () => {
    setShowChoosePlan(true);
  };

  const handleBillingClick = () => {
    setShowBillingHistory(true);
  };

  const handleSelectPlan = (plan: any, billingCycle: "monthly" | "annual") => {
    setSelectedBillingCycle(billingCycle);
    setSelectedPlan(plan);
    setShowPlanChange(true);
    setShowChoosePlan(false);
  };

  const handleBackToChoosePlan = () => {
    setShowPlanChange(false);
    setShowChoosePlan(true);
  };

  const handleBackToPricing = () => {
    setShowChoosePlan(false);
    setShowPlanChange(false);
    setSelectedPlan(null);
  };

  const handleConfirmPlanChange = () => {
    handleBackToPricing();
  };

  if (loadingCurrentSub) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (subError) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600">
            Error loading subscription data. Please try again later.
          </p>
          <AppButton
            label="Retry"
            handleClick={() => window.location.reload()}
            additionalClassNames={["mt-4"]}
          />
        </div>
      </div>
    );
  }

  if (showBillingHistory) {
    return (
      <BillingHistoryPage
        onBack={() => setShowBillingHistory(false)}
        currentSubData={currentSubData}
      />
    );
  }

  if (showChoosePlan) {
    return (
      <ChoosePlanPage
        currentPlanId={packageName}
        currentPlanName={packageName}
        isCorporate={userInfo?.recruiter?.is_corporate === 1}
        onBack={handleBackToPricing}
        onSelectPlan={handleSelectPlan}
        subscriptionData={currentSubData}
      />
    );
  }


  if (showPlanChange && selectedPlan) {
    const selectedPlanPrice =
      selectedBillingCycle === "monthly"
        ? selectedPlan.monthlyPrice
        : selectedPlan.annualPrice;

    const currentPlanForChange = {
      id: currentSubData?.package_id || "",
      name: currentSubData?.package_name || "Free",
      price: selectedBillingCycle === "monthly" ? monthlyPrice : annuallyPrice,
      features:
        currentSubData?.rules?.map((rule: any) => rule.description) || [],
    };

    const selectedPlanForChange = {
      id: selectedPlan.id,
      name: selectedPlan.name,
      price: selectedPlanPrice,
      features: selectedPlan.features || [],
    };

    const billingInfo = {
      credit: calculateCredit(),
      daysLeft: currentSubData?.subscription?.remaining_days || 0,
      newPrice: selectedPlanPrice,
      chargeToday: calculateChargeToday(selectedPlanPrice),
      nextBilling: currentSubData?.subscription?.next_payment_date || "N/A",
    };

    const dataPreservationMessages = [
      "All existing applicant data is preserved and accessible",
      "SmartScreen scores are no longer active but not deleted",
      "You can upgrade at any time to restore full access",
      "No loss of data before the downgrade takes effect",
    ];

    return (
      <PlanChangePage
        currentPlan={currentPlanForChange}
        selectedPlan={selectedPlanForChange}
        onBack={handleBackToPricing}
        onCancel={handleBackToChoosePlan}
        onConfirm={handleConfirmPlanChange}
        billingInfo={billingInfo}
        dataPreservationMessages={dataPreservationMessages}
        billingCycle={selectedBillingCycle}
      />
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Top Actions */}
        <div className="mb-6 flex justify-end gap-3">
          <AppButton
            label="Purchase Plan"
            additionalClassNames={["px-6 py-2"]}
            handleClick={handleUpgradeClick}
          />
          <AppButton
            label="Billing History"
            additionalClassNames={["px-6 py-2"]}
            handleClick={handleBillingClick}
          />
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 p-1 rounded-full flex items-center">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2 rounded-full text-sm ${billingCycle === "monthly"
                ? "bg-[#0B0F5B] text-white"
                : "text-gray-600"
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-6 py-2 rounded-full text-sm flex items-center gap-2 ${billingCycle === "annual"
                ? "bg-[#0B0F5B] text-white"
                : "text-gray-600"
                }`}
            >
              Annual
              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Current Plan */}
        <div className="bg-primary text-white rounded-lg flex justify-between p-6 mb-8">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ThunderboltOutlined className="text-white text-base" />
              Current Plan: {packageName}
            </h2>
            <p>{packageDescription}</p>
          </div>

          {monthlyPrice > 0 && (
            <div>
              <h2 className="text-lg font-semibold">
                ₦{billingCycle === "monthly" ? monthlyPrice : annuallyPrice}
              </h2>
              <p>/{billingCycle === "monthly" ? "month" : "year"}</p>
            </div>
          )}
        </div>

        {/* Alert */}
        {(isApproachingLimit || hasReachedLimit) && (
          <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-lg p-4 flex gap-3 mb-8">
            <WarningOutlined className="text-yellow-600 text-xl" />
            <div>
              <p className="font-medium text-yellow-800">
                {hasReachedLimit
                  ? "Usage Limit Reached"
                  : "Approaching Usage Limit"}
              </p>
              <p className="text-sm text-yellow-700">
                {hasReachedLimit
                  ? "You've reached your usage limits for some features. Upgrade your plan to continue."
                  : "You're approaching your usage limits. Upgrade your plan to avoid interruptions."}
              </p>
            </div>
          </div>
        )}

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {usageCards.map((item) => (
            <div
              key={item.key}
              className="bg-white rounded-lg border p-5 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {item.icon}
                  <span className="text-sm font-medium text-gray-700">
                    {formatRuleLabel(item.label)}
                  </span>
                </div>
                <span
                  className={`${item.statusBg} ${item.statusTextColor} text-xs px-2 py-1 rounded-full`}
                >
                  {item.statusText}
                </span>
              </div>
              <span className="text-xs text-gray-500 block mb-2">
                {item.used} of {item.total} used ({item.remaining} remaining)
              </span>
              <Progress
                percent={item.percentage}
                showInfo={false}
                strokeColor={item.percentage >= 100 ? "#ef4444" : "#22c55e"}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {item.percentage}% used
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Upgrade CTA */}
        <div className="bg-blue-50 border rounded-lg p-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h2 className="text-xl font-semibold mb-2">
              Unlock Advanced Features
            </h2>
            <ul className="space-y-2">
              <li className="flex items-center text-sm">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 inline-block"></span>
                Advanced candidate Search Filters
              </li>
              <li className="flex items-center text-sm">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 inline-block"></span>
                Resume Database Premium Access
              </li>
              <li className="flex items-center text-sm">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 inline-block"></span>
                Unlimited Messaging with Candidates
              </li>
              <li className="flex items-center text-sm">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 inline-block"></span>
                Premium customer support
              </li>
            </ul>
          </div>
          <AppButton
            label="Upgrade Plan"
            additionalClassNames={[
              "bg-primary text-white px-8 py-3 rounded-md font-semibold",
            ]}
            handleClick={handleUpgradeClick}
          />
        </div>
      </div>
    </div>
  );
};
